import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { StatusBadge, Tag } from '@/components/StatusBadge'
import {
  Money,
  MonoValue,
  PIIField,
  RelativeTime,
  RiskScore,
  UserCell,
} from '@/components/DataDisplay'
import type { AdminColumn, ColumnType } from './types'

/** Ustun turiga qarab standart tekislash. Raqam/pul — o'ngga (SKILL §6). */
export function alignOf(col: AdminColumn<unknown>): 'left' | 'right' | 'center' {
  if (col.align) return col.align
  if (col.type === 'number' || col.type === 'money') return 'right'
  if (col.type === 'bool') return 'center'
  return 'left'
}

const NUMERIC: ReadonlySet<ColumnType> = new Set(['number', 'money', 'risk', 'id'])

export function isNumericType(type: ColumnType): boolean {
  return NUMERIC.has(type)
}

function empty(): ReactNode {
  return <span className="text-tertiary">—</span>
}

/**
 * Ustun turiga qarab yagona render. Ekranlar ko'rinishni QO'LDA yozmaydi —
 * shu sabab 614 ekran bir xil xulq-atvorga ega bo'ladi.
 */
export function renderCell<T>(col: AdminColumn<T>, row: T, onRevealPii?: (col: AdminColumn<T>, row: T) => boolean | Promise<boolean>): ReactNode {
  if (col.render) return col.render(row)

  const raw = col.accessor
    ? col.accessor(row)
    : (row as Record<string, unknown>)[col.key]

  // Diqqat: `false` bu ro'yxatga TUSHMAYDI — `bool` ustunida u haqiqiy
  // qiymat va quyida ✓/— sifatida chiziladi.
  if (raw === null || raw === undefined || raw === '') return empty()

  switch (col.type) {
    case 'id':
      return <MonoValue value={String(raw)} truncate={String(raw).length > 20 ? 8 : undefined} />

    case 'text':
      return <span className="block truncate">{String(raw)}</span>

    case 'longtext':
      return (
        <span className="line-clamp-2 text-secondary" title={String(raw)}>
          {String(raw)}
        </span>
      )

    case 'number':
      return <span className="block text-right font-mono text-sm tabular">{Number(raw).toLocaleString()}</span>

    case 'money':
      return <Money amount={Number(raw)} currency={col.currency ?? 'UZS'} />

    case 'date':
      return <RelativeTime value={raw as string} className="text-secondary" />

    case 'enum': {
      const opt = col.options?.find((o) => o.value === String(raw))
      if (opt?.status) return <StatusBadge status={opt.status} label={opt.label} />
      return <Tag>{opt?.label ?? String(raw)}</Tag>
    }

    case 'bool':
      // SKILL §1: rang yolg'iz signal emas — belgi bilan.
      return raw ? (
        <span className="text-success" title="✓">
          ✓
        </span>
      ) : (
        <span className="text-tertiary">—</span>
      )

    case 'user': {
      const u = raw as { name?: string; username?: string; avatar_url?: string } | null
      if (!u) return empty()
      return <UserCell name={u.name ?? null} username={u.username ?? null} avatarUrl={u.avatar_url ?? null} />
    }

    case 'entity': {
      const e = raw as { label?: string; icon?: ReactNode } | string
      if (typeof e === 'string') return <span className="truncate text-accent">{e}</span>
      return (
        <span className="flex min-w-0 items-center gap-1.5">
          {e.icon}
          <span className="truncate text-accent">{e.label}</span>
        </span>
      )
    }

    case 'pii':
      return (
        <PIIField
          value={String(raw)}
          level={col.piiLevel ?? 'L2'}
          {...(onRevealPii ? { onReveal: () => onRevealPii(col, row) } : {})}
        />
      )

    case 'risk':
      return <RiskScore value={Number(raw)} />

    case 'tags': {
      const list = Array.isArray(raw) ? raw.map(String) : [String(raw)]
      const shown = list.slice(0, 3)
      const rest = list.length - shown.length
      return (
        <span className="flex flex-wrap items-center gap-1">
          {shown.map((v) => {
            const opt = col.options?.find((o) => o.value === v)
            return <Tag key={v}>{opt?.label ?? v}</Tag>
          })}
          {rest > 0 && <span className="text-xs text-tertiary">+{rest}</span>}
        </span>
      )
    }

    case 'json':
      return (
        <span className="font-mono text-xs text-tertiary" title={JSON.stringify(raw, null, 2)}>
          {'{…}'}
        </span>
      )

    default:
      return <span className="truncate">{String(raw)}</span>
  }
}

/** Eksport uchun tekis matn — ko'rinish emas, qiymat. */
export function cellToText<T>(col: AdminColumn<T>, row: T): string {
  const raw = col.accessor ? col.accessor(row) : (row as Record<string, unknown>)[col.key]
  if (raw === null || raw === undefined) return ''
  if (col.type === 'user') {
    const u = raw as { name?: string; username?: string }
    return u.name ?? (u.username ? `@${u.username}` : '')
  }
  if (col.type === 'tags' && Array.isArray(raw)) return raw.join('|')
  if (col.type === 'json') return JSON.stringify(raw)
  if (col.type === 'bool') return raw ? '1' : '0'
  return String(raw)
}

export function headerClass(col: AdminColumn<unknown>): string {
  const a = alignOf(col)
  return cn(
    'select-none truncate',
    a === 'right' && 'text-right',
    a === 'center' && 'text-center',
  )
}
